// Mobile hamburger nav — below app.css's 768px breakpoint, #sidebar/#panel
// become full-screen off-canvas overlays (see that stylesheet's own comment)
// instead of the desktop grid columns panels.ts's collapse/resize handles
// manage. This module only owns "which overlay (if any) is open" — sizing/
// positioning is entirely CSS's job via the .mobile-open class. Harmless to
// run unconditionally on desktop too: the trigger buttons are display:none
// there, so they're never clicked, and toggling a class on an element with
// no matching mobile CSS rule active has no visible effect.
// Canonical breakpoint check — also used by map.ts (mobile pipe-render
// gating) and tourSteps.ts (picking the mobile vs desktop tour), so it lives
// here rather than being duplicated per module.
const MOBILE_BREAKPOINT_PX = 768; // matches app.css's mobile nav breakpoint

export function isMobileViewport(): boolean {
  return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`).matches;
}

const sidebar = () => document.getElementById("sidebar")!;
const panel = () => document.getElementById("panel")!;
const backdrop = () => document.getElementById("mobile-backdrop")!;

/** Skips #sidebar/#panel's own slide-in/out CSS transition (app.css:
 * "transition: transform 200ms ease") for one class change, by disabling it,
 * forcing a synchronous layout flush (the getBoundingClientRect read — its
 * return value is unused, this call exists purely for the reflow
 * side-effect) so the change applies with nothing "in flight", then
 * restoring normal transition behaviour on the next frame for whatever real
 * user interaction comes next. Exists for the tour (tourSteps.ts): the same
 * class of bug just fixed for desktop's scroll-then-measure ordering — open/
 * closeAll's callers used to measure the target's position while the panel
 * was still mid-slide, landing the spotlight wherever the target happened to
 * be at that instant rather than its settled position. */
function withoutTransition(el: HTMLElement, mutate: () => void): void {
  el.style.transition = "none";
  mutate();
  void el.getBoundingClientRect();
  requestAnimationFrame(() => {
    el.style.transition = "";
  });
}

/** Closes whichever full-screen overlay (sidebar/panel) is open, if any.
 * Exported so tourSteps.ts's beforeShow callbacks can put mobile into a
 * known state (e.g. before a step that needs the map itself visible)
 * without needing to know which one was open. `instant` skips the slide
 * animation (see withoutTransition) — used by the tour so the very next
 * position measurement is already accurate; omitted (default false) for
 * real user interaction, which should keep the normal animated feel. */
export function closeAll(opts: { instant?: boolean } = {}): void {
  const s = sidebar();
  const p = panel();
  const apply = () => {
    s.classList.remove("mobile-open");
    p.classList.remove("mobile-open");
    backdrop().classList.remove("visible");
  };
  if (opts.instant) {
    withoutTransition(s, apply);
    p.style.transition = "none";
    requestAnimationFrame(() => {
      p.style.transition = "";
    });
  } else {
    apply();
  }
}

/** Opens `which` overlay full-screen, closing the other one first (only one
 * at a time — see the module comment). Exported for the same reason as
 * closeAll — a tour step whose target lives inside the sidebar or panel
 * needs to open it first via beforeShow. See closeAll for `instant`. */
export function open(which: "sidebar" | "panel", opts: { instant?: boolean } = {}): void {
  const target = which === "sidebar" ? sidebar() : panel();
  const other = which === "sidebar" ? panel() : sidebar();
  const apply = () => {
    other.classList.remove("mobile-open"); // only one full-screen overlay at a time
    target.classList.add("mobile-open");
    backdrop().classList.add("visible");
  };
  if (opts.instant) {
    withoutTransition(target, apply);
  } else {
    apply();
  }
}

export function initMobileNav(): void {
  document.getElementById("mobile-sidebar-toggle")?.addEventListener("click", () => open("sidebar"));
  document.getElementById("mobile-panel-toggle")?.addEventListener("click", () => open("panel"));
  document.getElementById("mobile-sidebar-close")?.addEventListener("click", () => closeAll());
  document.getElementById("mobile-panel-close")?.addEventListener("click", () => closeAll());
  backdrop().addEventListener("click", () => closeAll());
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeAll();
  });
}
